import { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, Loader, ChevronDown, ChevronUp, Settings, ChevronRight, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { getQuestionCategories, getQuestionsByCategory } from '../data/suggestedQuestions';
import { 
  createThread, 
  sendMessageToAssistant, 
  isAssistantServiceAvailable 
} from '../services/openaiAssistantService';

interface SourceDetail {
  fileId: string;
  displayName: string;
  fileName?: string | null;
  citationTexts: string[];
  indices: Array<{
    start: number;
    end: number;
    text: string;
  }>;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: string[] | SourceDetail[]; // Support ancien format (string[]) et nouveau (SourceDetail[])
}

interface AIAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuestion?: string | null;
}

export default function AIAssistantModal({ isOpen, onClose, initialQuestion }: AIAssistantModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isConfigured, setIsConfigured] = useState(() => isAssistantServiceAvailable());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sentInitialQuestionRef = useRef<string | null>(null);


  // Au premier rendu dans un navigateur, tenter de charger la config globale
  // depuis le backend pour remplir localStorage, afin d'éviter de devoir
  // resynchroniser manuellement dans l'interface Admin.
  useEffect(() => {
    const ensureAssistantConfig = async () => {
      if (typeof window === 'undefined') {
        return;
      }

      const localAssistantId = localStorage.getItem('prisme_assistant_id');
      if (localAssistantId) {
        return;
      }

      try {
        const response = await fetch('/api/prisme/assistant/config', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          const assistantId = data.assistant_id || data.assistantId;
          const vectorStoreId = data.vector_store_id || data.vectorStoreId;

          if (assistantId) {
            localStorage.setItem('prisme_assistant_id', assistantId);
            setIsConfigured(true);
          }
          if (vectorStoreId) {
            localStorage.setItem('prisme_vector_store_id', vectorStoreId);
          }
          if (!assistantId) {
            setIsConfigured(false);
          }
        }
      } catch (error) {
        console.warn('Impossible de charger la config globale de l’Assistant PRIsME:', error);
        setIsConfigured(isAssistantServiceAvailable());
      } finally {
      }
    };

    ensureAssistantConfig();
  }, []);

  // Initialize thread when modal opens
  useEffect(() => {
    if (isOpen && !threadId && isConfigured) {
      initializeThread();
    }
  }, [isOpen, isConfigured, threadId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && !isLoading && !isInitializing) {
      inputRef.current?.focus();
    }
  }, [isOpen, isLoading, isInitializing]);

  const initializeThread = async () => {
    setIsInitializing(true);
    try {
      const newThreadId = await createThread();
      setThreadId(newThreadId);
      
      // Add welcome message
      setMessages([{
        id: '0',
        role: 'assistant',
        content: `👋 **Salut! Je suis LEX, l'Assistant PRIsME de BZ Inc.**

Direct et pragmatique, je t'aide selon les standards BZ.

💡 **Clique sur une question suggérée** ou pose-moi ta question!`,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Failed to initialize thread:', error);
      setMessages([{
        id: '0',
        role: 'assistant',
        content: `❌ **Erreur d'initialisation**

Impossible de créer la session.

**Détail serveur :** ${error instanceof Error ? error.message : 'Erreur inconnue'}

Vérifie que:
1. Ta session PRIsME BZ est encore valide
2. La clé API OpenAI est configurée côté serveur
3. L'Assistant a été créé (Admin → Setup Assistant)`,
        timestamp: new Date()
      }]);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isLoading || !threadId) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowSuggestions(false);

    try {
      // Send to OpenAI Assistant
      const response = await sendMessageToAssistant(threadId, content);
      
      if (response.success) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
          sources: response.sources
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Error message
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `❌ ${response.message}

Essaie de reformuler ta question ou contacte l'administrateur si le problème persiste.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `❌ Erreur de communication: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      sentInitialQuestionRef.current = null;
      return;
    }

    const question = initialQuestion?.trim();
    if (!question || !threadId || isLoading || sentInitialQuestionRef.current === question) return;

    sentInitialQuestionRef.current = question;
    void handleSendMessage(question);
  }, [isOpen, initialQuestion, threadId, isLoading]);

  const handleSuggestedQuestion = (question: string) => {
    handleSendMessage(question);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const categories = getQuestionCategories();

  if (!isOpen) return null;

  // Composant pour afficher une source avec détails expandables
  const SourceCard = ({ displayName, citationTexts, fileId, isSimple }: { 
    displayName: string; 
    citationTexts: string[]; 
    fileId?: string;
    isSimple: boolean;
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if (isSimple) {
      // Ancien format simple (juste le nom)
      return (
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
          <FileText className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{displayName}</span>
        </div>
      );
    }
    
    // Extraire les informations des citations (format OpenAI: "[8:18 source...]")
    const parseCitation = (citation: string) => {
      const match = citation.match(/\[(\d+):(\d+)\s+(.+?)\]/);
      if (match) {
        return {
          start: parseInt(match[1]),
          end: parseInt(match[2]),
          text: match[3] || citation
        };
      }
      return { start: 0, end: 0, text: citation };
    };
    
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-blue-50 transition-colors text-left group"
        >
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="p-1.5 bg-blue-100 rounded group-hover:bg-blue-200 transition-colors">
              <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-800 truncate">{displayName}</div>
              {citationTexts.length > 0 && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {citationTexts.length} référence{citationTexts.length > 1 ? 's' : ''} dans la réponse
                </div>
              )}
            </div>
          </div>
          <ChevronRight 
            className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
              isExpanded ? 'rotate-90' : ''
            }`} 
          />
        </button>
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 py-3 bg-gradient-to-br from-gray-50 to-blue-50/30 border-t border-gray-200 space-y-3">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  📍 Références dans la réponse
                </div>
                
                {citationTexts.length > 0 ? (
                  <div className="space-y-2">
                    {citationTexts.map((citation, idx) => {
                      const parsed = parseCitation(citation);
                      return (
                        <div key={idx} className="bg-white rounded-lg px-3 py-2.5 border border-gray-200 shadow-sm">
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                Référence #{idx + 1}
                              </span>
                              {parsed.start > 0 && parsed.end > 0 && (
                                <span className="text-xs text-gray-500 font-mono">
                                  Positions {parsed.start}-{parsed.end}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-gray-700 mt-1.5 bg-gray-50 rounded px-2.5 py-2 border-l-2 border-blue-400">
                            <div className="font-mono text-xs text-gray-600 mb-1">Citation:</div>
                            <div className="text-gray-800 leading-relaxed">{parsed.text}</div>
                          </div>
                          <div className="text-xs text-gray-500 mt-2 italic">
                            Cette référence pointe vers le document: <span className="font-semibold">{displayName}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    Aucune citation détaillée disponible
                  </div>
                )}
                
                {fileId && (
                  <div className="pt-2 mt-2 border-t border-gray-200">
                    <div className="text-xs text-gray-500">
                      <span className="font-semibold">Identifiant technique:</span>
                      <div className="font-mono text-gray-400 bg-gray-100 rounded px-2 py-1 mt-1 break-all">
                        {fileId}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Sparkles className="w-8 h-8" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">LEX - Assistant PRIsME BZ Inc.</h2>
              <p className="text-sm text-blue-100">
                {isConfigured 
                  ? '🤖 Connecté - Powered by OpenAI Assistants API' 
                  : '⚠️ Non configuré - Exécute le setup'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map(message => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-6 py-4 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-800 shadow-md'
                }`}
              >
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                  
                  {message.sources && message.sources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 mb-2">📚 Sources ({message.sources.length}):</p>
                      <div className="space-y-2">
                        {message.sources.map((source, idx) => {
                          // Support ancien format (string) et nouveau format (SourceDetail)
                          const isStringSource = typeof source === 'string';
                          const sourceDetail = isStringSource ? null : (source as SourceDetail);
                          const displayName = isStringSource ? (source as string) : (sourceDetail?.displayName || 'Source inconnue');
                          const citationTexts = sourceDetail?.citationTexts || [];
                          const fileId = sourceDetail?.fileId;
                          
                          return (
                            <SourceCard
                              key={idx}
                              displayName={displayName}
                              citationTexts={citationTexts}
                              fileId={fileId}
                              isSimple={isStringSource}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-xs mt-2 opacity-60">
                  {message.timestamp.toLocaleTimeString('fr-CA', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-2xl px-6 py-4 shadow-md">
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>LEX réfléchit...</span>
                </div>
              </div>
            </div>
          )}

          {isInitializing && (
            <div className="flex justify-center">
              <div className="bg-blue-50 border border-blue-200 rounded-2xl px-6 py-4">
                <div className="flex items-center gap-2 text-blue-700">
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Initialisation de la session...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Questions */}
        {showSuggestions && messages.length <= 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3 hover:text-blue-600 transition-colors"
            >
              {showSuggestions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Questions suggérées (Top 20)
            </button>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
              {categories.map(category => {
                const questions = getQuestionsByCategory(category);
                return questions.slice(0, 3).map(q => (
                  <button
                    key={q.id}
                    onClick={() => handleSuggestedQuestion(q.question)}
                    disabled={isLoading || !threadId}
                    className="text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    <span className="text-xs font-semibold text-blue-600 mb-1 block">{category}</span>
                    <span className="text-gray-700 group-hover:text-blue-700">{q.question}</span>
                  </button>
                ));
              })}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-6 border-t border-gray-200 bg-white rounded-b-2xl">
          {!isConfigured && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Settings className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">Assistant non configuré</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Va dans la page Admin pour exécuter le setup initial (bouton dans le header).
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || !threadId || !isConfigured}
              placeholder={
                !isConfigured 
                  ? "Assistant non configuré..." 
                  : !threadId 
                  ? "Initialisation..." 
                  : "Pose-moi ta question..."
              }
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <button
              onClick={() => handleSendMessage(inputValue)}
              disabled={isLoading || !inputValue.trim() || !threadId || !isConfigured}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold"
            >
              <Send className="w-5 h-5" />
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
